// Run detail (FD §22.3) — one agent working on one task. The header says
// what the run is and where it is in its lifecycle (RunTrack); if the run is
// waiting on a decision, the approval sits directly beneath it. Below that,
// five views: Overview, Activity, Terminal, Changes, Details. Fed live by
// useSSE while the run is active.

import { LiveDuration } from '../../components/live-duration'
import { Markdown } from '../../components/markdown'
import { useState, type ReactNode } from 'react'
import { Link, useParams } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { Run, RunEvent } from '@goblic/shared'
import { RunStatus, TERMINAL_RUN_STATUSES } from '@goblic/shared'
import { approvalsApi, runsApi } from '../../lib/api-client'
import { useWorkspaceId } from '../../lib/auth'
import { useSSE } from '../../hooks/useSSE'
import { useLookups } from '../../hooks/useLookups'
import { ApprovalPanel, type AgentQuestionAnswer } from '../../components/approval-panel'
import { TerminalView } from '../../components/terminal-view'
import { DiffViewer } from '../../components/diff-viewer'
import { DecisionRunOverview } from '../../components/runs/decision-run'
import { RunTrack } from '../../components/run-track'
import { ErrorNote, Facts, Notice, Panel, Tabs } from '../../components/ui'
import {
  ClayIcon,
  ClayList,
  MomentArt,
  RoleAvatar,
} from '../../components/goblin-art'
import { useDialogs } from '../../components/dialogs'
import {
  HandedOffNotice,
  RunChainNav,
  RunRecoveryPanel,
  useRunRecovery,
} from '../../components/run-recovery-panel'
import {
  describeCheckpointEvent,
  formatUntil,
  isCheckpointEvent,
  isScheduledResume,
} from '../../lib/run-recovery'
import {
  KbPageLink,
  RunKnowledgePanel,
  kbRunEventOf,
} from '../../components/knowledge/run-knowledge'
import {
  RunReportPanel,
  describeReportEvent,
  reportRunEventOf,
} from '../../components/reports/run-report'
import {
  formatDateTime,
  formatDuration,
  formatMs,
  formatNumber,
  formatRunCost,
  formatTime,
  humanize,
  shortId,
} from '../../lib/format'

type RunTab = 'overview' | 'activity' | 'terminal' | 'changes' | 'details'

export function RunDetailPage() {
  // `from` addresses the route by id, not its URL path — the id carries the
  // pathless `_authenticated` parent's id prefix (see router.tsx) even
  // though the actual URL (and every `Link to="/runs/$runId"`) has none.
  const { runId } = useParams({ from: '/_authenticated/runs/$runId' })
  const workspaceId = useWorkspaceId()
  const lookups = useLookups()
  const [tab, setTab] = useState<RunTab>('overview')
  const { confirm } = useDialogs()
  const queryClient = useQueryClient()

  const runQuery = useQuery({
    queryKey: ['run', runId],
    queryFn: () => runsApi.get(runId),
    // Poll while the run is still in flight (header fields like cost/tokens
    // don't yet arrive over SSE below); stops once a terminal status is
    // reached, since TanStack Query re-evaluates `refetchInterval` after
    // every fetch using the latest `data`.
    refetchInterval: (query) => {
      const status = query.state.data?.status
      return status && TERMINAL_RUN_STATUSES.includes(status) ? false : 5_000
    },
  })

  const pendingApprovalQuery = useQuery({
    queryKey: ['run', runId, 'pending-approval'],
    queryFn: () => approvalsApi.list({ workspaceId, runId, status: 'pending' }),
    enabled:
      runQuery.data?.status === RunStatus.AwaitingApproval ||
      runQuery.data?.status === RunStatus.Paused,
  })

  const { events, status: sseStatus } = useSSE(runId)
  const recoveryQuery = useRunRecovery(runQuery.data)
  // The notice belongs to the run that was handed off (the page is reused when navigating to the new run).
  const [handedOff, setHandedOff] = useState<{ from: string; to: string } | null>(null)

  function invalidateRun(): void {
    void queryClient.invalidateQueries({ queryKey: ['run', runId] })
    void queryClient.invalidateQueries({ queryKey: ['approvals', workspaceId] })
  }

  const stopMutation = useMutation({
    mutationFn: () => runsApi.stop(runId, {}),
    onSuccess: () => {
      invalidateRun()
    },
  })

  const approveMutation = useMutation({
    mutationFn: ({
      approvalId,
      actionHash,
      note,
    }: {
      approvalId: string
      actionHash: string
      note?: string
    }) => approvalsApi.approve(approvalId, { actionHash, note }),
    onSuccess: invalidateRun,
  })
  const rejectMutation = useMutation({
    mutationFn: ({
      approvalId,
      actionHash,
      note,
    }: {
      approvalId: string
      actionHash: string
      note?: string
    }) => approvalsApi.reject(approvalId, { actionHash, note }),
    onSuccess: invalidateRun,
  })
  const instructMutation = useMutation({
    mutationFn: ({
      approvalId,
      actionHash,
      instruction,
    }: {
      approvalId: string
      actionHash: string
      instruction: string
    }) => approvalsApi.instruct(approvalId, { actionHash, instruction }),
  })
  const answerMutation = useMutation({
    mutationFn: ({
      approvalId,
      actionHash,
      answer,
    }: {
      approvalId: string
      actionHash: string
      answer: AgentQuestionAnswer
    }) => approvalsApi.answer(approvalId, { actionHash, ...answer }),
    onSuccess: invalidateRun,
  })

  if (runQuery.isError) {
    return (
      <div className="panel">
        <ErrorNote error={runQuery.error} what="this run" />
      </div>
    )
  }
  if (runQuery.isLoading || !runQuery.data) {
    return <p className="text-base text-ink-3">Loading run…</p>
  }

  const run = runQuery.data
  const isTerminal = TERMINAL_RUN_STATUSES.includes(run.status)
  // A decision run has no sandbox, git, PR, diff or checkpoints (it only reads).
  const isDecision = run.kind === 'decision'
  const pendingApproval = pendingApprovalQuery.data?.items[0]
  const task = lookups.task(run.taskId)
  const changedFiles = events.filter(
    (e): e is Extract<RunEvent, { type: 'file_changed' }> =>
      e.type === 'file_changed'
  )

  return (
    <div>
      <div className="mb-2 text-sm text-ink-3">
        <Link to="/runs" className="hover:text-ink">
          Runs
        </Link>
        <span className="px-1.5">/</span>
        <span className="font-mono">{shortId(run.id)}</span>
      </div>

      <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 max-w-3xl items-center gap-4">
          <RoleAvatar roleName={lookups.roleName(run.roleId)} size={56} />
          <div className="min-w-0">
            <h1 className="font-display text-xl font-semibold tracking-[-0.01em] text-ink">
              {task?.title ?? `Run ${shortId(run.id)}`}
            </h1>
            <p className="mt-1 text-base text-ink-2">
              <span className="font-medium text-action">
                {lookups.agentName(run.agentId)}
              </span>
              {run.projectId ? (
                <>
                  {' '}
                  {isTerminal ? 'worked' : 'working'} in{' '}
                  {lookups.projectName(run.projectId)}
                </>
              ) : null}
              {run.model ? <> with {run.model}</> : null}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isTerminal ? (
            <button
              type="button"
              className="btn-danger-quiet"
              disabled={stopMutation.isPending}
              onClick={async () => {
                const stop = await confirm({
                  kind: 'warning',
                  title: 'Stop this run?',
                  message:
                    'The agent stops right away and its sandbox is removed. Work it hasn’t reported yet is lost.',
                  confirmLabel: 'Stop run',
                  cancelLabel: 'Keep running',
                })
                if (stop) stopMutation.mutate()
              }}
            >
              {stopMutation.isPending ? 'Stopping…' : 'Stop run'}
            </button>
          ) : null}
          {/* TODO: Pause (POST /runs/:id/pause) and Retry (POST /runs/:id/retry
              with a same-snapshot/current-config choice) once the API has them. */}
        </div>
      </header>

      <RunChainNav run={run} chain={recoveryQuery.data?.chain} />

      <section className="panel mb-6 px-5 pb-4 pt-5">
        <RunTrack
          status={run.status}
          hasStarted={run.startedAt != null}
          captions={{
            0: formatTime(run.queuedAt),
            2: run.startedAt ? formatTime(run.startedAt) : undefined,
            4: run.endedAt ? formatTime(run.endedAt) : undefined,
          }}
        />
        <div className="mt-5 flex flex-wrap gap-x-8 gap-y-2 border-t border-line pt-4 text-base">
          <Stat
            label="Duration"
            value={<LiveDuration startedAt={run.startedAt} endedAt={run.endedAt} />}
          />
          <Stat label="Tokens" value={formatNumber(run.tokensUsed)} />
          <Stat label="Cost" value={<RunCost run={run} runCostDisplay={lookups.runCostDisplay} />} />
          {isScheduledResume(run) ? (
            <Stat label="Resumes at" value={formatUntil(run.resumeAfter)} />
          ) : null}
          {isDecision ? null : (
            <Stat
              label="Files changed"
              value={String(
                run.result?.changed_files.length ?? changedFiles.length
              )}
            />
          )}
          {run.pullRequestUrl && !isDecision ? (
            <a
              href={run.pullRequestUrl}
              target="_blank"
              rel="noreferrer"
              className="link flex items-center gap-1.5 text-sm"
            >
              <ClayIcon name="pull-request" size={18} />
              Pull request
            </a>
          ) : null}
          <span className="ml-auto flex items-center gap-2 text-sm text-ink-3">
            <span
              aria-hidden
              className={`h-1.5 w-1.5 rounded-full ${sseStatus === 'open' ? 'bg-ok' : 'bg-ink-3'}`}
            />
            {sseStatus === 'open'
              ? 'Live'
              : isTerminal
                ? 'Finished'
                : 'Reconnecting'}
          </span>
        </div>
      </section>

      {(run.status === RunStatus.AwaitingApproval ||
        run.status === RunStatus.Paused) &&
      pendingApproval ? (
        <div className="mb-6">
          <ApprovalPanel
            approval={pendingApproval}
            showRunLink={false}
            agentName={lookups.agentName}
            projectName={lookups.projectName}
            isSubmitting={
              approveMutation.isPending ||
              rejectMutation.isPending ||
              instructMutation.isPending ||
              answerMutation.isPending
            }
            onApprove={(approvalId, actionHash, note) =>
              approveMutation.mutateAsync({ approvalId, actionHash, note })
            }
            onReject={(approvalId, actionHash, note) =>
              rejectMutation.mutateAsync({ approvalId, actionHash, note })
            }
            onInstruct={(approvalId, actionHash, instruction) =>
              instructMutation.mutateAsync({
                approvalId,
                actionHash,
                instruction,
              })
            }
            onAnswer={(approvalId, actionHash, answer) =>
              answerMutation.mutateAsync({ approvalId, actionHash, answer })
            }
          />
        </div>
      ) : null}

      {recoveryQuery.data && !pendingApproval && !isDecision ? (
        <RunRecoveryPanel
          key={`${run.id}-${run.status}`}
          run={run}
          state={recoveryQuery.data}
          onHandedOff={(to) => setHandedOff({ from: run.id, to })}
        />
      ) : null}
      {handedOff && handedOff.from === run.id ? (
        <div className="mb-6">
          <HandedOffNotice childRunId={handedOff.to} />
        </div>
      ) : null}

      <div className="mb-5">
        <Tabs
          label="Run views"
          value={tab}
          onChange={setTab}
          options={[
            { value: 'overview', label: 'Overview' },
            { value: 'activity', label: 'Activity', count: events.length },
            ...(isDecision
              ? []
              : [
                  { value: 'terminal' as const, label: 'Terminal' },
                  {
                    value: 'changes' as const,
                    label: 'Changes',
                    count: changedFiles.length,
                  },
                ]),
            { value: 'details', label: 'Details' },
          ]}
        />
      </div>

      {tab === 'overview' && isDecision ? (
        <DecisionRunOverview
          run={run}
          events={events}
          cost={<RunCost run={run} runCostDisplay={lookups.runCostDisplay} />}
        />
      ) : null}
      {tab === 'overview' && !isDecision ? (
        <OverviewTab
          run={run}
          instruction={task ? task.instruction : undefined}
          events={events}
        />
      ) : null}
      {tab === 'activity' ? (
        <ActivityTab events={events} agentName={lookups.agentName} />
      ) : null}
      {tab === 'terminal' && !isDecision ? <TerminalView events={events} /> : null}
      {tab === 'changes' && !isDecision ? (
        <ChangesTab runId={runId} run={run} events={events} />
      ) : null}
      {tab === 'details' ? <DetailsTab run={run} lookups={lookups} /> : null}
    </div>
  )
}

function Stat({ label, value }: { label: string; value: ReactNode }) {
  return (
    <span>
      <span className="text-ink-3">{label}</span>{' '}
      <span className="font-semibold tabular-nums text-ink">{value}</span>
    </span>
  )
}

/** A run's cost, honoring the workspace's "Run costs" setting (lib/format.ts's `formatRunCost`). */
function RunCost({
  run,
  runCostDisplay,
}: {
  run: Pick<Run, 'costCents' | 'billing'>
  runCostDisplay: 'estimate' | 'included'
}) {
  const cost = formatRunCost(run, runCostDisplay)
  return <span title={cost.tooltip}>{cost.text}</span>
}

function OverviewTab({
  run,
  instruction,
  events,
}: {
  run: Run
  instruction?: string
  events: RunEvent[]
}) {
  const result = run.result
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
      <Panel title="Result">
        {result ? (
          <div className="space-y-5">
            <MomentArt
              moment={run.status === 'completed' ? 'success' : 'failed'}
              size={96}
              className="float-right -mr-2 -mt-3 ml-3"
            />
            <div className="text-base text-ink">
              <Markdown>{result.summary}</Markdown>
            </div>
            {result.blockers.length ? (
              <Notice tone={run.status === 'completed' ? 'signal' : 'error'}>
                <p className="font-medium">
                  {run.status === 'completed' ? 'Still open' : 'Blocked by'}
                </p>
                <ClayList
                  className="mt-1.5"
                  icon={
                    run.status === 'completed' ? 'hourglass' : 'wrench-broken'
                  }
                  items={result.blockers}
                />
              </Notice>
            ) : null}
            <Facts
              items={[
                {
                  label: 'Pull request',
                  icon: <ClayIcon name="pull-request" size={28} />,
                  value: result.pull_request ? (
                    <a
                      href={result.pull_request.url}
                      target="_blank"
                      rel="noreferrer"
                      className="link"
                    >
                      #{result.pull_request.number}{' '}
                      {result.pull_request.isDraft ? '(draft)' : ''}
                    </a>
                  ) : run.pullRequestUrl ? (
                    // This run didn't push itself (e.g. a Test/Review step),
                    // but the task's shared branch already has one.
                    <a
                      href={run.pullRequestUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="link"
                    >
                      Open on the task
                    </a>
                  ) : (
                    'None'
                  ),
                },
                {
                  label: 'Branch',
                  icon: <ClayIcon name="branch" size={28} />,
                  value: result.branch ? (
                    result.delivery?.pushed && result.delivery.repositoryUrl ? (
                      <a
                        href={`${result.delivery.repositoryUrl}/tree/${result.branch}`}
                        target="_blank"
                        rel="noreferrer"
                        className="link font-mono text-sm"
                      >
                        {result.branch}
                      </a>
                    ) : (
                      <span>
                        <code className="font-mono text-sm">
                          {result.branch}
                        </code>
                        <span className="ml-1.5 text-sm text-ink-3">
                          (not pushed)
                        </span>
                      </span>
                    )
                  ) : (
                    '—'
                  ),
                },
                {
                  label: 'Tests',
                  icon: <ClayIcon name="checks" size={28} />,
                  value: result.tests
                    ? `${result.tests.passed} passed, ${result.tests.failed} failed`
                    : 'Not run',
                },
                {
                  label: 'Suggested next step',
                  icon: <ClayIcon name="signpost" size={28} />,
                  value: humanize(result.recommended_next_state),
                },
              ]}
            />
          </div>
        ) : (
          <p className="text-base text-ink-3">
            {TERMINAL_RUN_STATUSES.includes(run.status) &&
            run.status !== RunStatus.HandedOff ? (
              <MomentArt
                moment="failed"
                size={80}
                className="float-right -mt-2 ml-3"
              />
            ) : null}
            {run.status === RunStatus.HandedOff
              ? 'This run handed its work over to another agent before it finished. The result is on the new run.'
              : TERMINAL_RUN_STATUSES.includes(run.status)
              ? 'This run ended without reporting a result.'
              : 'The agent reports a summary, test results and a pull request link here when it finishes.'}
          </p>
        )}
      </Panel>
      <div className="space-y-6">
        <Panel title="Task">
          <p className="whitespace-pre-wrap text-base text-ink-2">
            {instruction || 'No instruction on record.'}
          </p>
        </Panel>
        <RunReportPanel
          taskId={run.taskId}
          events={events}
          live={!TERMINAL_RUN_STATUSES.includes(run.status)}
        />
        <RunKnowledgePanel
          runId={run.id}
          live={!TERMINAL_RUN_STATUSES.includes(run.status)}
          events={events}
        />
      </div>
    </div>
  )
}

/** Human label per event type (FD §22.4: "22:31 Run gestart", "22:32 Repository gekloond", ...). */
function describe(
  event: RunEvent,
  agentName?: (id: string | null | undefined) => string
): {
  text: string
  detail?: string
  tone?: 'danger' | 'signal' | 'ok'
  /** A knowledge page the event is about (kb_read / kb_write). */
  kbPage?: { space: string; path: string }
  /** Another run of the handoff chain the event points at. */
  runLink?: { runId: string; label: string }
} {
  const report = reportRunEventOf(event)
  if (report) return describeReportEvent(report)
  if (isCheckpointEvent(event)) return describeCheckpointEvent(event, agentName)
  const kb = kbRunEventOf(event)
  if (kb) {
    const kbPage = kb.path ? { space: kb.space, path: kb.path } : undefined
    const rev = kb.rev != null ? `rev ${kb.rev}` : undefined
    if (kb.type === 'kb_read') {
      return { text: 'Read knowledge page', detail: rev, kbPage }
    }
    return {
      text:
        kb.status === 'proposed'
          ? 'Proposed a change to knowledge page'
          : kb.status === 'conflict'
            ? 'Knowledge page changed meanwhile, not saved'
            : 'Wrote knowledge page',
      detail: [rev, kb.message].filter(Boolean).join(' · ') || undefined,
      tone: kb.status === 'conflict' ? 'signal' : kb.status === 'applied' ? 'ok' : undefined,
      kbPage,
    }
  }
  switch (event.type) {
    case 'run_started':
      return { text: 'Run started' }
    case 'context_compiled':
      return { text: 'Project context compiled' }
    case 'repository_cloned':
      return {
        text: `Cloned ${event.repositoryUrl.replace(/^https?:\/\//, '')}`,
        detail: `${event.branch} at ${event.baseCommit.slice(0, 8)}`,
      }
    case 'skill_loaded':
      return {
        text: `Loaded skill ${event.skillId}`,
        detail: event.skillVersion,
      }
    case 'agent_message':
      return { text: event.message }
    case 'file_changed':
      return { text: `${humanize(event.changeType)} ${event.path}` }
    case 'tool_call_started':
      return {
        text: `Called ${event.toolName}`,
        detail: JSON.stringify(event.redactedParams),
      }
    case 'tool_call_completed':
      return {
        text: `Tool ${event.resultStatus === 'success' ? 'finished' : event.resultStatus} in ${Math.round(event.durationMs)}ms`,
        tone: event.resultStatus === 'success' ? undefined : 'danger',
        detail: JSON.stringify(event.redactedOutput),
      }
    case 'tests_run':
      return {
        text: `Tests: ${event.passed} passed, ${event.failed} failed`,
        tone: event.failed > 0 ? 'danger' : 'ok',
      }
    case 'approval_requested':
      return { text: 'Asked for your approval', tone: 'signal' }
    case 'approval_resolved':
      return {
        text: event.approved ? 'Approval granted' : 'Approval rejected',
        tone: event.approved ? 'ok' : 'danger',
      }
    case 'status_changed':
      return {
        text:
          event.from && event.from !== event.to
            ? `${humanize(event.from)} to ${humanize(event.to).toLowerCase()}`
            : `Now ${humanize(event.to).toLowerCase()}`,
      }
    case 'error':
      return {
        text: event.message,
        tone: 'danger',
        detail: event.isRetryable ? 'Can be retried' : undefined,
      }
    case 'run_completed':
      return { text: 'Run finished', tone: 'ok' }
    default:
      return { text: humanize((event as RunEvent).type) }
  }
}

/** Group events by subtask ID for delegate tool calls (06 §2.7). */
function groupEventsBySubtask(events: RunEvent[]): (RunEvent | { _type: 'subtask_group'; subtaskId: string; subtaskTitle: string; subtaskIndex: number; events: RunEvent[] })[] {
  const result: (RunEvent | { _type: 'subtask_group'; subtaskId: string; subtaskTitle: string; subtaskIndex: number; events: RunEvent[] })[] = []
  const grouped = new Map<string, RunEvent[]>()

  for (const event of events) {
    if (event.subtask) {
      if (!grouped.has(event.subtask.id)) {
        grouped.set(event.subtask.id, [])
      }
      grouped.get(event.subtask.id)!.push(event)
    } else {
      result.push(event)
    }
  }

  // Sort result back to maintain order: reconstruct by finding where subtask groups belong
  const finalResult: (RunEvent | { _type: 'subtask_group'; subtaskId: string; subtaskTitle: string; subtaskIndex: number; events: RunEvent[] })[] = []
  let groupedSoFar: Map<string, boolean> = new Map()

  for (const event of events) {
    if (event.subtask && !groupedSoFar.has(event.subtask.id)) {
      // First occurrence of this subtask group
      const subtaskEvents = grouped.get(event.subtask.id)!
      finalResult.push({
        _type: 'subtask_group',
        subtaskId: event.subtask.id,
        subtaskTitle: event.subtask.title,
        subtaskIndex: event.subtask.index,
        events: subtaskEvents,
      })
      groupedSoFar.set(event.subtask.id, true)
    } else if (!event.subtask) {
      finalResult.push(event)
    }
  }

  return finalResult
}

/** The real per-sub-task shape `subtasks/manager.ts` puts on its end event's `redactedOutput` (06 §2.7, QA finding #6). */
interface SubtaskEndStats {
  status: 'completed' | 'failed' | 'budget' | 'timeout' | 'cancelled'
  model: string
  modelCalls: number
  tokens: number
  durationMs: number
  changedFiles: string[]
  revertedFiles: string[]
}

function isSubtaskEndEvent(
  event: RunEvent,
): event is Extract<RunEvent, { type: 'tool_call_completed' }> & { redactedOutput: SubtaskEndStats } {
  return event.type === 'tool_call_completed' && event.toolCallId.startsWith('subtask-end-') && typeof event.redactedOutput === 'object' && event.redactedOutput !== null
}

export function ActivityTab({
  events,
  agentName,
}: {
  events: RunEvent[]
  agentName: (id: string | null | undefined) => string
}) {
  const [showTools, setShowTools] = useState(false)
  const [expandedSubtasks, setExpandedSubtasks] = useState<Set<string>>(new Set())

  // Grouped from the *full*, unfiltered event list: a sub-task's own end
  // event (06 §2.7) is itself a `tool_call_completed` event, so it would be
  // hidden along with every other tool call when "Show tool calls" is off —
  // but `getSubtaskStats` (below) needs it regardless of that toggle, which
  // only hides individual tool-call *lines* once a group is expanded (see
  // `visibleSubEvents`), same as it always hid them at the top level.
  const grouped = groupEventsBySubtask(events)
  const isToolCall = (e: RunEvent): boolean => e.type === 'tool_call_started' || e.type === 'tool_call_completed'
  const visibleTopLevel = grouped.filter((item) => showTools || !('type' in item) || !isToolCall(item))
  const hidden = grouped.length - visibleTopLevel.length
  const dot = { danger: 'bg-danger', signal: 'bg-signal', ok: 'bg-ok' }

  const toggleSubtask = (subtaskId: string) => {
    const newExpanded = new Set(expandedSubtasks)
    if (newExpanded.has(subtaskId)) {
      newExpanded.delete(subtaskId)
    } else {
      newExpanded.add(subtaskId)
    }
    setExpandedSubtasks(newExpanded)
  }

  /** Reads the sub-task's own end event directly — no guessing from a count of `tool_call_completed` events or a hardcoded status (the previous, placeholder version of this function). `undefined` while the sub-task is still running (no end event recorded yet). */
  const getSubtaskStats = (subtaskEvents: RunEvent[]): SubtaskEndStats | undefined => {
    const end = subtaskEvents.find(isSubtaskEndEvent)
    return end?.redactedOutput
  }

  return (
    <Panel
      title="Activity"
      description="Everything the agent did, newest last."
      actions={
        <label className="flex items-center gap-2 text-sm text-ink-2">
          <input
            type="checkbox"
            checked={showTools}
            onChange={(e) => setShowTools(e.target.checked)}
            className="accent-[rgb(var(--moss))]"
          />
          Show tool calls
        </label>
      }
      flush
    >
      {visibleTopLevel.length === 0 ? (
        <p className="px-5 py-6 text-base text-ink-3">
          {events.length === 0
            ? 'Nothing yet. Events stream in live while the run works.'
            : `${hidden} tool calls hidden.`}
        </p>
      ) : (
        <ol className="divide-y divide-line">
          {visibleTopLevel.map((item) => {
            if ('_type' in item && item._type === 'subtask_group') {
              const isExpanded = expandedSubtasks.has(item.subtaskId)
              const stats = getSubtaskStats(item.events)
              const revertedCount = stats?.revertedFiles.length ?? 0
              const visibleSubEvents = showTools ? item.events : item.events.filter((e) => !isToolCall(e))

              return (
                <li key={`subtask-${item.subtaskId}`} className="divide-y divide-line">
                  <button
                    onClick={() => toggleSubtask(item.subtaskId)}
                    className="w-full grid grid-cols-[72px_12px_minmax(0,1fr)] gap-3 px-5 py-2.5 text-left hover:bg-surface-2"
                  >
                    <time className="pt-px font-mono text-xs text-ink-3">
                      —
                    </time>
                    <span
                      aria-hidden
                      className={`mt-1.5 h-1.5 w-1.5 rounded-full transition-transform ${
                        isExpanded ? 'rotate-90' : ''
                      } bg-ok`}
                    />
                    <div className="min-w-0">
                      <p className="text-base text-ink-2 font-medium">
                        Sub-task {item.subtaskIndex}: {item.subtaskTitle}
                        <span className="text-ink-3 font-normal">
                          {' '}
                          ·{' '}
                          {stats
                            ? `${stats.modelCalls} calls · ${formatNumber(stats.tokens)} tokens · ${formatMs(stats.durationMs)} · ${stats.status}`
                            : 'in progress'}
                        </span>
                      </p>
                      {revertedCount > 0 && (
                        <p className="mt-1 text-sm text-danger">
                          ⚠ {revertedCount} file{revertedCount === 1 ? '' : 's'} reverted
                        </p>
                      )}
                    </div>
                  </button>
                  {isExpanded && (
                    <ol className="bg-surface-2">
                      {visibleSubEvents.map((event) => {
                        const d = describe(event, agentName)
                        const prefix = `[sub ${item.subtaskIndex}] `

                        return (
                          <li
                            key={event.id}
                            className="grid grid-cols-[72px_12px_minmax(0,1fr)] gap-3 px-5 py-2.5"
                          >
                            <time className="pt-px font-mono text-xs text-ink-3">
                              {formatTime(event.timestamp)}
                            </time>
                            <span
                              aria-hidden
                              className={`mt-1.5 h-1.5 w-1.5 rounded-full ${d.tone ? dot[d.tone] : 'bg-line-strong'}`}
                            />
                            <div className="min-w-0">
                              <p
                                className={`whitespace-pre-wrap break-words text-base ${
                                  event.type === 'agent_message'
                                    ? 'text-ink'
                                    : 'text-ink-2'
                                }`}
                              >
                                {event.type === 'agent_message' && prefix}
                                {d.text}
                                {d.kbPage ? (
                                  <>
                                    {' '}
                                    <KbPageLink space={d.kbPage.space} path={d.kbPage.path} />
                                  </>
                                ) : null}
                              </p>
                              {d.detail ? (
                                <p className="mt-0.5 truncate font-mono text-xs text-ink-3">
                                  {d.detail}
                                </p>
                              ) : null}
                            </div>
                          </li>
                        )
                      })}
                    </ol>
                  )}
                </li>
              )
            } else {
              const event = item as RunEvent
              const d = describe(event, agentName)
              return (
                <li
                  key={event.id}
                  className="grid grid-cols-[72px_12px_minmax(0,1fr)] gap-3 px-5 py-2.5"
                >
                  <time className="pt-px font-mono text-xs text-ink-3">
                    {formatTime(event.timestamp)}
                  </time>
                  <span
                    aria-hidden
                    className={`mt-1.5 h-1.5 w-1.5 rounded-full ${d.tone ? dot[d.tone] : 'bg-line-strong'}`}
                  />
                  <div className="min-w-0">
                    <p
                      className={`whitespace-pre-wrap break-words text-base ${event.type === 'agent_message' ? 'text-ink' : 'text-ink-2'}`}
                    >
                      {d.text}
                      {d.kbPage ? (
                        <>
                          {' '}
                          <KbPageLink space={d.kbPage.space} path={d.kbPage.path} />
                        </>
                      ) : null}
                      {d.runLink ? (
                        <>
                          {' '}
                          <Link
                            to="/runs/$runId"
                            params={{ runId: d.runLink.runId }}
                            className="link text-sm"
                          >
                            {d.runLink.label}
                          </Link>
                        </>
                      ) : null}
                    </p>
                    {d.detail ? (
                      <p className="mt-0.5 truncate font-mono text-xs text-ink-3">
                        {d.detail}
                      </p>
                    ) : null}
                  </div>
                </li>
              )
            }
          })}
        </ol>
      )}
    </Panel>
  )
}

function ChangesTab({
  runId,
  run,
  events,
}: {
  runId: string
  run: Run
  events: RunEvent[]
}) {
  const tests = events.filter(
    (e): e is Extract<RunEvent, { type: 'tests_run' }> => e.type === 'tests_run'
  )
  const lastTests =
    tests[tests.length - 1] ??
    (run.result?.tests
      ? { passed: run.result.tests.passed, failed: run.result.tests.failed }
      : null)
  const files = [
    ...new Map(
      events
        .filter(
          (e): e is Extract<RunEvent, { type: 'file_changed' }> =>
            e.type === 'file_changed'
        )
        .map((e) => [e.path, e.changeType] as const)
    ),
  ]
  const fileList = files.length
    ? files
    : (run.result?.changed_files ?? []).map(
        (path) => [path, 'modified'] as const
      )
  const mark = {
    added: 'text-ok',
    modified: 'text-info',
    deleted: 'text-danger',
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_280px]">
        <Panel title="Files" flush>
          {fileList.length ? (
            <ul className="divide-y divide-line">
              {fileList.map(([path, change]) => (
                <li
                  key={path}
                  className="flex items-center gap-3 px-5 py-2 font-mono text-sm"
                >
                  <span className={`w-4 font-semibold ${mark[change]}`}>
                    {change === 'added'
                      ? 'A'
                      : change === 'deleted'
                        ? 'D'
                        : 'M'}
                  </span>
                  <span className="truncate text-ink">{path}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="px-5 py-6 text-base text-ink-3">
              No files changed yet.
            </p>
          )}
        </Panel>
        <Panel title="Tests">
          {lastTests ? (
            <p className="text-base">
              <span className="font-semibold text-ok">
                {lastTests.passed} passed
              </span>
              <span className="text-ink-3">, </span>
              <span
                className={
                  lastTests.failed > 0
                    ? 'font-semibold text-danger'
                    : 'text-ink-2'
                }
              >
                {lastTests.failed} failed
              </span>
            </p>
          ) : (
            <p className="text-base text-ink-3">
              The agent hasn’t run tests yet.
            </p>
          )}
        </Panel>
      </div>
      <DiffViewer runId={runId} />
    </div>
  )
}

function DetailsTab({
  run,
  lookups,
}: {
  run: Run
  lookups: ReturnType<typeof useLookups>
}) {
  return (
    <div className="space-y-6">
      <Panel
        title="Configuration"
        description="Frozen when the run started, so it can be reproduced."
      >
        <Facts
          columns={3}
          items={[
            { label: 'Agent', value: lookups.agentName(run.agentId) },
            {
              label: 'Role',
              value: run.roleId
                ? `${lookups.roleName(run.roleId)} v${run.roleVersion}`
                : '—',
            },
            { label: 'Project', value: lookups.projectName(run.projectId) },
            {
              label: 'Executor',
              value: lookups.executorName(run.executorProfileId),
            },
            { label: 'Model', value: run.model || '—' },
            ...(run.kind === 'decision'
              ? []
              : [
                  {
                    label: 'Sandbox',
                    value: run.sandboxProfileId
                      ? humanize(run.sandboxProfileId)
                      : '—',
                  },
                  // Which Docker host it ran on (sandbox-hosts ontwerp §11).
                  { label: 'Sandbox host', value: run.sandboxHost?.name ?? '—' },
                ]),
          ]}
        />
      </Panel>
      <Panel title="Timing and cost">
        <Facts
          columns={3}
          items={[
            { label: 'Queued', value: formatDateTime(run.queuedAt) },
            { label: 'Started', value: formatDateTime(run.startedAt) },
            { label: 'Ended', value: formatDateTime(run.endedAt) },
            { label: 'Tokens', value: formatNumber(run.tokensUsed) },
            { label: 'Cost', value: <RunCost run={run} runCostDisplay={lookups.runCostDisplay} /> },
          ]}
        />
        {/* TODO: cost breakdown by tool call / model call once per-call cost is tracked (FD §25). */}
      </Panel>
      <Panel title="Identifiers">
        <Facts
          items={[
            {
              label: 'Run',
              value: <code className="font-mono text-sm">{run.id}</code>,
            },
            {
              label: 'Task',
              value: <code className="font-mono text-sm">{run.taskId}</code>,
            },
            {
              label: 'Context hash',
              value: (
                <code className="font-mono text-sm">
                  {run.contextHash || '—'}
                </code>
              ),
            },
            {
              label: 'Instructions',
              value: run.contextVersion
                ? `${run.contextVersion} (${run.contextMode === 'goblic' ? 'Goblic' : 'native'})`
                : '—',
            },
          ]}
        />
        {/* TODO: render run.contextSnapshot (FD §13.3), artifacts (GET /runs/:id/artifacts,
            downloads audit-logged per FD §24.2) and the run's audit trail
            (GET /audit-log?entityType=run&entityId=:runId). */}
      </Panel>
    </div>
  )
}
